import KitchenTimer from '../../shared/components/KitchenTimer';
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getRecipe, RecipeDetail } from './api';
import './recipes.css';
import DataTable from '../../shared/components/DataTable';

const RecipeSOPView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { sessionToken } = useAuth();
  const [data, setData] = useState<RecipeDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cooking, setCooking] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [multiplier, setMultiplier] = useState(1);

  const load = useCallback(() => {
    if (!id) return;
    getRecipe(sessionToken, parseInt(id, 10))
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [sessionToken, id]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="rcp-loading">Loading SOP...</div>;
  if (error) return <div className="rcp-error">{error}</div>;
  if (!data) return null;

  const { recipe, ingredients, steps } = data;

  const totalTime = (recipe.prep_time_minutes || 0) + (recipe.cook_time_minutes || 0);

  return (
    <div className="rcp-sop-page">
      {/* Nav (hidden on print) */}
      <div className="rcp-sop-nav" style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between' }}>
        <Link to={`/recipes/${id}`} className="rcp-back-link">← Back to Recipe</Link>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="rcp-btn rcp-sop-print-btn" onClick={() => window.print()} style={{ position: 'static' }}>
            🖨️ Print / Save PDF
          </button>
        </div>
      </div>

      {/* SOP Header */}
      <div className="rcp-sop-header">
        <h1>{recipe.name}</h1>
        <p>{recipe.recipe_type}{recipe.category ? ` · ${recipe.category}` : ''}</p>
        <p style={{ marginTop: '0.25rem', fontSize: '0.8rem', color: '#94a3b8' }}>
          Version {recipe.version} · Status: {recipe.status}
        </p>
      </div>

      {/* Meta block */}
      <div className="rcp-sop-meta">
        {recipe.yield_quantity && (
          <div className="rcp-sop-meta-item">
            <label>Yield</label>
            <span>{recipe.yield_quantity} {recipe.yield_unit || ''}</span>
          </div>
        )}
        {recipe.prep_time_minutes != null && (
          <div className="rcp-sop-meta-item">
            <label>Prep Time</label>
            <span>{recipe.prep_time_minutes} min</span>
          </div>
        )}
        {recipe.cook_time_minutes != null && (
          <div className="rcp-sop-meta-item">
            <label>Cook Time</label>
            <span>{recipe.cook_time_minutes} min</span>
          </div>
        )}
        {totalTime > 0 && (
          <div className="rcp-sop-meta-item">
            <label>Total Time</label>
            <span>{totalTime} min</span>
          </div>
        )}
        {recipe.shelf_life && (
          <div className="rcp-sop-meta-item">
            <label>Shelf Life</label>
            <span>{recipe.shelf_life}</span>
          </div>
        )}
      </div>

      <div className="cs-toolbar cs-cooking-controls"><label>Batch multiplier <input aria-label="Batch multiplier" type="number" inputMode="decimal" min="0.1" step="0.5" value={multiplier} onChange={e => setMultiplier(Math.max(0.1, Number(e.target.value) || 1))} /></label><button onClick={() => setCooking(!cooking)}>{cooking ? 'Show all steps' : 'Start cooking mode'}</button></div>
      {cooking && steps.length > 0 && <div className="cs-sticky cs-cooking-controls"><p>Step {stepIndex + 1} of {steps.length}</p><div className="cs-actions"><button disabled={stepIndex === 0} onClick={() => setStepIndex(i => i - 1)}>Previous</button><button disabled={stepIndex === steps.length - 1} onClick={() => setStepIndex(i => i + 1)}>Next step</button></div></div>}
      <KitchenTimer />
      {/* Ingredients */}
      {ingredients.length > 0 && (
        <div className="rcp-sop-section">
          <h2>Ingredients</h2>
          <DataTable
            rows={ingredients}
            rowKey={(ing) => ing.id}
            caption="Ingredients"
            empty="No ingredients."
            columns={[
              {
                key: 'ingredient', header: 'Ingredient', priority: 'primary',
                render: (ing, i) => (
                  <>
                    <span className="rcp-text-muted">{i + 1}. </span>
                    {ing.ingredient_type === 'Sub Recipe'
                      ? `${ing.sub_recipe_name} (sub-recipe)`
                      : ing.ingredient_name || '—'}
                    {ing.is_optional ? <em className="rcp-text-muted"> (optional)</em> : null}
                  </>
                ),
              },
              {
                key: 'qty', header: 'Quantity', priority: 'summary',
                render: (ing) => (
                  <strong>{ing.quantity != null ? `${Number((Number(ing.quantity) * multiplier).toFixed(3))} ${ing.unit || ''}` : (ing.quantity_text || '—')}</strong>
                ),
              },
              { key: 'prep', header: 'Prep Note', priority: 'summary', render: (ing) => ing.preparation_note || '—' },
            ]}
          />
        </div>
      )}

      {/* Steps */}
      {steps.length > 0 && (
        <div className="rcp-sop-section">
          <h2>Procedure</h2>
          <ol className="rcp-sop-steps">
            {steps.filter((_, index) => !cooking || index === stepIndex).map(step => (
              <li key={step.id} className="rcp-sop-step">
                <div className={`rcp-sop-step-num ${step.critical_control_point ? 'ccp-step' : ''}`}>
                  {step.step_number}
                </div>
                <div className="rcp-sop-step-body">
                  <div style={cooking ? { fontSize: '1.4rem', lineHeight: 1.6 } : undefined}>{step.instruction}</div>
                  {step.station && (
                    <span className="rcp-tag rcp-tag-station" style={{ marginTop: '0.25rem', display: 'inline-block' }}>
                      {step.station}
                    </span>
                  )}
                  {step.critical_control_point && (
                    <span className="rcp-tag rcp-tag-ccp" style={{ marginLeft: '0.25rem' }}>⚠️ Critical Control Point</span>
                  )}
                  {step.staff_note && (
                    <div className="rcp-sop-step-note">📌 Staff note: {step.staff_note}</div>
                  )}
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Storage */}
      {(recipe.storage_instructions || recipe.shelf_life) && (
        <div className="rcp-sop-section">
          <h2>Storage & Handling</h2>
          <div className="rcp-sop-storage">
            {recipe.shelf_life && <p><strong>Shelf life:</strong> {recipe.shelf_life}</p>}
            {recipe.storage_instructions && <p>{recipe.storage_instructions}</p>}
          </div>
        </div>
      )}

      {/* Notes */}
      {recipe.notes && (
        <div className="rcp-sop-section">
          <h2>Notes</h2>
          <p style={{ fontSize: '0.9rem', lineHeight: 1.6 }}>{recipe.notes}</p>
        </div>
      )}

      {/* Footer */}
      <div style={{ marginTop: '2rem', paddingTop: '1rem', borderTop: '1px solid #e2e8f0', fontSize: '0.75rem', color: '#94a3b8', textAlign: 'center' }}>
        Chai Social CafeOps · Recipe SOP · Version {recipe.version} · {new Date().toLocaleDateString()}
      </div>

    </div>
  );
};

export default RecipeSOPView;
